<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import StandardToggle from './StandardToggle.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import { useField } from 'vee-validate';
import { getTranslation } from '@/composables/data-display-utils';
import type { Locales, ProjectModuleDto } from '@/services/types/api.types';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    /** Base field path in the form, e.g. "ctaButton", "config.ctaButtons.enabled" */
    name: string;
    languageEnabled: Locales[];
    /** The blocks of its block's parent, in order: the button may scroll to any but its own */
    blocks: ProjectModuleDto[];
    /** The block the button belongs to */
    blockId: number;
    /** Optional label override */
    label?: string;
    /** Whether the button may scroll to another block of its block's parent, which depends on the block type and host */
    allowModuleLinks?: boolean;
  }>(),
  { allowModuleLinks: true },
);

// Build dynamic field paths
const enabledPath = computed(() => `${props.name}.enabled`);
const textPath = computed(() => `${props.name}.text`);
const linkTypePath = computed(() => `${props.name}.linkType`);
const urlPath = computed(() => `${props.name}.url`);
const moduleIdPath = computed(() => `${props.name}.moduleId`);
const modules = computed(() =>
  props.blocks
    .filter((module) => module.id !== props.blockId)
    .map((module) => ({
      label: getTranslation(module, 'title', props.languageEnabled),
      value: module.id,
    })),
);

// Get values directly from vee-validate
const { value: enabled } = useField<boolean>(enabledPath.value);
const { value: linkType } = useField<'url' | 'module'>(linkTypePath.value);

const linkTypeOptions = computed(() => [
  { label: t('project-module.ctaButtons.button.contentType.url'), value: 'url' },
  ...(props.allowModuleLinks
    ? [{ label: t('project-module.ctaButtons.button.contentType.module'), value: 'module' }]
    : []),
]);

const displayLabel = computed(() => props.label || t('project-module.ctaButtons.enable-button'));
</script>

<template>
  <div class="generic-project-module-button-config space-y-4">
    <!-- Toggle to enable the button -->
    <standard-toggle :name="enabledPath" :label="displayLabel" outlined />

    <!-- Configuration fields — only shown when enabled -->
    <div v-if="enabled" class="pl-6 space-y-5 border-l-2 border-gray-200">
      <!-- Button text (localized) -->
      <localized-input
        :name="textPath"
        :label="t('project-module.ctaButtons.button.text')"
        outlined
        :language_enabled="props.languageEnabled"
      />

      <!-- Link type selector -->
      <localized-select
        :name="linkTypePath"
        :label="t('project-module.ctaButtons.button.linkType')"
        :options="linkTypeOptions"
        emit-value
        outlined
        map-options
      />

      <div v-if="allowModuleLinks && linkType === 'module'">
        <localized-select
          :label="t('project-module.ctaButtons.button.contentType.module')"
          :name="moduleIdPath"
          :options="modules"
          emit-value
          outlined
          map-options
        />
      </div>
      <div v-else-if="linkType === 'url'">
        <localized-input
          :name="urlPath"
          :label="t('project-module.ctaButtons.button.contentType.url')"
          outlined
          :language_enabled="props.languageEnabled"
        />
      </div>
    </div>
  </div>
</template>
